/**
 * Card model.
 *
 * A "value" card has a numeric value 1-13. A "wild" card has no value and comes
 * in four kinds, each switched on or off per table (see `WildConfig`):
 *   smash     — annihilates every card beneath it on its own stack.
 *   lightning — vaporizes the top card of each of the other four stacks.
 *   swap      — at the end of the turn its player swaps the top cards of two piles.
 *   peek      — its player sees their own next card until they play it.
 * `wildIndex` is the kind's position in `WILD_KINDS`, so it is also what card ids
 * are built from (see engine/deck.ts).
 */
export type CardId = string;

export type WildKind = "smash" | "lightning" | "swap" | "peek";

export type Suit = "diamonds" | "hearts" | "clubs" | "spades";

/**
 * Suits in deck order: a full unit's four copies of a value are one of each,
 * in this order, so copy index and suit are the same thing.
 *
 * Suits carry NO rules weight — every comparison in the engine is on `value`
 * alone. They exist so a player can tell one 7 from another and recognise a
 * stack they have seen before, which is the whole memory game (see the note on
 * stack order in DESIGN_raw.md).
 */
export const SUITS: readonly Suit[] = ["diamonds", "hearts", "clubs", "spades"];

/**
 * A half unit has room for only two copies of each value, so it uses two of the
 * four suits. Diamonds and spades: they are the most distinct pair both in
 * silhouette and in tone (see SUIT_TONE in pixi/cardArt.ts), which matters
 * because the half deck is the default configuration.
 */
export const HALF_UNIT_SUITS: readonly Suit[] = ["diamonds", "spades"];

export interface ValueCard {
  id: CardId;
  kind: "value";
  value: number; // 1-13
  /** One of the four suits. Cosmetic — no rule reads it. */
  suit: Suit;
  /** Which physical deck (0-based) this card came from, when playing with 2 decks. */
  deckIndex: number;
}

export interface WildCard {
  id: CardId;
  kind: "wild";
  /** The kind's index in `WILD_KINDS`. */
  wildIndex: 0 | 1 | 2 | 3;
  wildKind: WildKind;
  deckIndex: number;
}

export type Card = ValueCard | WildCard;

/**
 * The kinds in deck order. The order is load-bearing: it is `wildIndex`, it is
 * the `w{n}` in every wild's card id, and it is the order `describeDeckConfig`
 * names them in — and the Go engine's `WildKinds` has to agree on all three.
 * Append new kinds; never reorder.
 */
export const WILD_KINDS: readonly WildKind[] = ["smash", "lightning", "swap", "peek"];

export function isWild(card: Card): card is WildCard {
  return card.kind === "wild";
}

export function isWildKind(card: Card, kind: WildKind): boolean {
  return card.kind === "wild" && card.wildKind === kind;
}

export function isValue(card: Card): card is ValueCard {
  return card.kind === "value";
}

export const MIN_VALUE = 1;
export const MAX_VALUE = 13;
